import React, { ReactNode } from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { CreateUpdateBoardMemberForm } from '../CreateUpdateBoardMemberForm'
import { societyBoardCreateSchema } from '../../../../forms/schemas/society_board_create'
import { BoardRole } from '../../../../types/board-roles'
import {
  ESocietyBoardRole,
  ISocietiesModel,
} from '../../../../interfaces/models/societies.interfaces'
import { createUserRole, deleteUserRole } from '../../../../api/societies'
import { i18nSocietyType } from '../../../../i18n'

interface CreateUpdateBoardMemberModalProps {
  show: boolean
  close: () => void
  children?: React.ReactNode
  userId?: string
  society: ISocietiesModel
}

export const CreateUpdateBoardMemberModal = ({
  userId,
  children,
  show,
  society,
  close,
}: CreateUpdateBoardMemberModalProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!userId

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateBoardMemberModal.flashMessage.updateRoleFailure'
      : 'createUpdateBoardMemberModal.flashMessage.createRoleFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateBoardMemberModal.flashMessage.updateRoleSuccess'
      : 'createUpdateBoardMemberModal.flashMessage.createRoleSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const { mutate: mutateUpdateBoardRole, isPending: isUpdatingRole } =
    useMutation({
      mutationFn: async (data: InferType<typeof societyBoardCreateSchema>) => {
        const deleteStatus = await deleteUserRole([
          {
            societyId: society?._id as string,
            userId: data.userId,
            boardRole: true,
            nominatingCommitteeRole: true,
          },
        ])
        if (deleteStatus) {
          const roleData =
            data.role === BoardRole.NOMINATING_COMMITTEE
              ? { nominatingCommitteeRole: true }
              : { boardRole: data.role as ESocietyBoardRole }
          const status = await createUserRole({
            body: [
              {
                societyId: society?._id as string,
                userId: data.userId,
                ...roleData,
              },
            ],
            sendNotifications: true,
          })

          if (!status) throw new Error()
          return status
        }
        throw new Error()
      },
      onSuccess: () => {
        showSuccessMessage()
        close()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyBoardCreateSchema>
  ): Promise<void> => {
    mutateUpdateBoardRole(data)
  }

  const onError = (error: unknown): void => {
    captureException(error)
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const loading = isUpdatingRole

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'createUpdateBoardMemberModal.editBoardMemberTitle'
                  : 'createUpdateBoardMemberModal.addNewBoardMemberTitle',
                { context: i18nSocietyType(society?.societyTypeDisplayName) }
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <CreateUpdateBoardMemberForm
                society={society}
                onError={onError}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
                userId={userId}
              />
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
